import * as React from 'react';
import {
  BlocksProvider,
  LayersProvider,
  PagesProvider,
  SelectorsProvider,
  StylesProvider,
  TraitsProvider,
} from '@grapesjs/react';
import {
  mdiBrush,
  mdiLayers,
  mdiViewGridPlus,
  mdiTextBoxMultiple,
  mdiCog,
} from '@mdi/js';
import Icon from '@mdi/react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useState } from 'react';
import CustomBlockManager from './CustomBlockManager';
import { MAIN_BORDER_COLOR, cx } from './common';
import CustomPageManager from './CustomPageManager';
import CustomLayerManager from './CustomLayerManager';
import CustomSelectorManager from './CustomSelectorManager';
import CustomStyleManager from './CustomStyleManager';
import CustomTraitManager from './CustomTraitManager';
import { View, StyleSheet, useWindowDimensions } from 'react-native';
import { ScrollView } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { styled } from '@mui/material/styles';
import { Box, Paper } from '@mui/material';
import * as Animatable from 'react-native-animatable';

// Styled components for modern theming
const StyledTabsContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme?.palette?.mode === 'dark' ? '#2d2d2d' : '#4a90e2',
  boxShadow: theme?.palette?.mode === 'dark'
    ? '0 4px 20px rgba(0,0,0,0.3)'
    : '0 4px 20px rgba(74,144,226,0.3)',
  borderRadius: '12px 12px 0 0',
  overflow: 'hidden',
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minWidth: 0,
  padding: '12px 16px',
  transition: 'all 0.3s ease',
  '&.Mui-selected': {
    backgroundColor: theme?.palette?.mode === 'dark' ? '#4a90e2' : 'rgba(255,255,255,0.2)',
    color: 'white',
    fontWeight: 600,
  },
  '&:hover': {
    backgroundColor: theme?.palette?.mode === 'dark' ? '#3d3d3d' : 'rgba(255,255,255,0.1)',
  },
}));

const StyledScrollContainer = styled(Box)(({ theme }) => ({
  maxHeight: '80vh',
  alignSelf: 'stretch',
  minWidth: '100%',
  backgroundColor: theme?.palette?.mode === 'dark' ? '#1a1a1a' : '#ffffff',
  borderRadius: '0 0 12px 12px',
}));

const TabContentContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme?.palette?.mode === 'dark' ? '#252525' : '#f8f9fa',
  borderRadius: 12,
  padding: 16,
  margin: '8px 0',
  boxShadow: theme?.palette?.mode === 'dark'
    ? '0 2px 8px rgba(0,0,0,0.3)'
    : '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
}));

const defaultTabProps = {
  className: '!min-w-0',
};

export default function RightSidebar({
  className, disablePages, type
}: React.HTMLAttributes<HTMLDivElement> & { disablePages?: boolean, type?: string }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const { width, height } = useWindowDimensions();
  const theme = themeSelector();

  return (
    <Animatable.View animation="slideInLeft" style={{
      zIndex: -1,
      flex: 1,
      shadowOffset: { height: 0, width: 10 },
      shadowRadius: 10,
      shadowOpacity: .05
    }}>
      <View style={{ flex: 1, maxHeight: 70 }}>
        <StyledTabsContainer>
          <Tabs
            value={selectedTab}
            component={(props) => (
              <ScrollView
                horizontal
                {...props}
                showsHorizontalScrollIndicator={false}
                style={{ maxHeight: 60, flexDirection: 'row' }}
              />
            )}
            onChange={(_, v) => setSelectedTab(v)}
            variant="fullWidth"
            indicatorColor="secondary"
            textColor="inherit"
          >
            <StyledTab
              label={<Icon size={1} path={mdiViewGridPlus} color="white" />}
            />
            <StyledTab
              label={<Icon size={1} color="white" path={mdiBrush} />}
            />
            <StyledTab
              label={<Icon size={1} color="white" path={mdiCog} />}
            />
            <StyledTab
              label={<Icon size={1} color="white" path={mdiLayers} />}
            />
            {!disablePages && (
              <StyledTab
                label={<Icon size={1} path={mdiTextBoxMultiple} color="white" />}
              />
            )}
          </Tabs>
        </StyledTabsContainer>
      </View>

      <StyledScrollContainer>
        {selectedTab === 0 && (
          <TabContentContainer>
            <BlocksProvider>
              {(props) => <CustomBlockManager type={type} {...props} />}
            </BlocksProvider>
          </TabContentContainer>
        )}

        {selectedTab === 1 && (
          <>
            {type !== 'image' && (
              <TabContentContainer>
                <SelectorsProvider>
                  {(props) => (
                    <CustomSelectorManager {...props} />
                  )}
                </SelectorsProvider>
              </TabContentContainer>
            )}
            <TabContentContainer>
              <StylesProvider>
                {(props) => (
                  <CustomStyleManager {...props} type={type} />
                )}
              </StylesProvider>
            </TabContentContainer>
          </>
        )}

        {selectedTab === 2 && (
          <TabContentContainer>
            <TraitsProvider>
              {(props) => (
                <CustomTraitManager {...props} />
              )}
            </TraitsProvider>
          </TabContentContainer>
        )}

        {selectedTab === 3 && (
          <TabContentContainer>
            <LayersProvider>
              {(props) => props.root && <CustomLayerManager {...props} root={props.root} />}
            </LayersProvider>
          </TabContentContainer>
        )}

        {!disablePages && type !== 'image' && selectedTab === 4 && (
          <TabContentContainer>
            <PagesProvider>
              {(props) => props.selected && <CustomPageManager {...props} selected={props.selected} />}
            </PagesProvider>
          </TabContentContainer>
        )}
      </StyledScrollContainer>
    </Animatable.View>
  );
}

const style = StyleSheet.create({
  container: { flex: 1, alignSelf: "stretch", minWidth: "100%", gap: 20 }
})